import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './ReportStudentListing.css';
import Swal from 'sweetalert2';
import { reportStudentListingAPI, authorizationAPI } from '../../services/api';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import ReportIssueModal from '../StudentUniform/ReportIssueModal';

// Register ag-grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

/**
 * Report-StudentListing Component
 * Migrated from Report-StudentListing.aspx
 *
 * Displays detailed student uniform listing for a single school
 * Features:
 * - Role-based PPD/School filtering (OWN_SCHOOL, OWN_DISTRICT, ALL)
 * - Calls stored procedure LoadStudentListingByYearBySchool_sp
 * - Displays DUN and PAR information for selected school
 * - Shows student uniform sizes in detail with ag-grid
 * - Excel/PDF export functionality
 */
const ReportStudentListing = () => {
  // ========== HOOKS ==========
  const { t } = useTranslation(['reports', 'common']);

  // ========== STATE MANAGEMENT ==========

  // Auth/User State
  const [user, setUser] = useState(null);
  const [resourcePermissions, setResourcePermissions] = useState(null);

  // Filter State
  const [ppds, setPPDs] = useState([]);
  const [schools, setSchools] = useState([]);
  const [activities, setActivities] = useState([]);

  const [selectedPPD, setSelectedPPD] = useState('');
  const [selectedSchool, setSelectedSchool] = useState('');
  const [selectedActivity, setSelectedActivity] = useState('');

  // School Info State
  const [schoolInfo, setSchoolInfo] = useState(null);

  // Data State
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ag-grid State
  const [gridApi, setGridApi] = useState(null);

  // Report Issue modal states
  const [showReportIssueModal, setShowReportIssueModal] = useState(false);
  const [screenshot, setScreenshot] = useState(null);

  // ========== AG-GRID CONFIGURATION ==========

  // Column definitions for ag-grid
  const columnDefs = useMemo(() => [
    {
      headerName: t('reports:studentListing.columns.no'),
      valueGetter: 'node.rowIndex + 1',
      width: 70,
      pinned: 'left',
      cellStyle: { textAlign: 'center' }
    },
    {
      headerName: t('reports:studentListing.columns.name'),
      field: 'name',
      width: 300,
      pinned: 'left',
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    {
      headerName: t('reports:studentListing.columns.noKP'),
      field: 'noKP',
      width: 140,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => {
        if (params.value) {
          // Mask IC: show only last 4 digits (e.g., ********1234)
          return '********' + params.value.slice(-4);
        }
        return '';
      }
    },
    {
      headerName: t('reports:studentListing.columns.tahun'),
      field: 'tahun',
      width: 120,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellStyle: { textAlign: 'center' }
    },
    {
      headerName: t('reports:studentListing.columns.kelas'),
      field: 'kelas',
      width: 120,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellStyle: { textAlign: 'center' }
    },
    {
      headerName: t('reports:studentListing.columns.jantina'),
      field: 'jantina',
      width: 100,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellStyle: { textAlign: 'center' }
    },
    {
      headerName: t('reports:studentListing.columns.bajuPutih'),
      field: 'bajuPutih',
      width: 120,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellStyle: { textAlign: 'center' }
    },
    {
      headerName: t('reports:studentListing.columns.seluarPanjang'),
      field: 'seluarPanjang',
      width: 140,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellStyle: { textAlign: 'center' }
    },
    {
      headerName: t('reports:studentListing.columns.seluarPendek'),
      field: 'seluarPendek',
      width: 140,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellStyle: { textAlign: 'center' }
    },
    {
      headerName: t('reports:studentListing.columns.bajuKurung'),
      field: 'bajuKurung',
      width: 140,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellStyle: { textAlign: 'center' }
    },
    {
      headerName: t('reports:studentListing.columns.kainKurung'),
      field: 'kainKurung',
      width: 140,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellStyle: { textAlign: 'center' }
    },
    {
      headerName: t('reports:studentListing.columns.pinafore'),
      field: 'pinafore',
      width: 120,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellStyle: { textAlign: 'center' }
    },
    {
      headerName: t('reports:studentListing.columns.kasut'),
      field: 'kasut',
      width: 100,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellStyle: { textAlign: 'center' }
    }
  ], [t]);

  // Default column configuration
  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    filter: false
  }), []);

  // Grid options
  const gridOptions = useMemo(() => ({
    pagination: true,
    paginationPageSize: 50,
    paginationPageSizeSelector: [30, 50, 100],
    domLayout: 'autoHeight',
    suppressRowClickSelection: true,
    rowSelection: 'multiple'
  }), []);

  // Grid ready callback
  const onGridReady = (params) => {
    setGridApi(params.api);
    console.log('[ReportStudentListing] ag-grid ready');
  };

  // ========== EFFECTS ==========

  // Load user and permissions on mount
  useEffect(() => {
    loadUser();
    loadResourcePermissions();
  }, []);

  // Load PPDs when user is loaded
  useEffect(() => {
    if (user) {
      loadPPDs();
    }
  }, [user]);

  // Load schools when PPD changes
  useEffect(() => {
    if (selectedPPD) {
      loadSchools(selectedPPD);
      setSelectedSchool('');
      setSelectedActivity('');
      setActivities([]);
      setSchoolInfo(null);
      setReportData(null);
    }
  }, [selectedPPD]);

  // Load activities and school info when school changes
  useEffect(() => {
    if (selectedSchool) {
      loadActivities(selectedSchool);
      loadSchoolInfo(selectedSchool);
      setSelectedActivity('');
      setReportData(null);
    }
  }, [selectedSchool]);

  // Auto-load data when activity changes (using activity's enrolYear)
  useEffect(() => {
    if (selectedActivity && selectedSchool && activities.length > 0) {
      // Get the enrolYear from the selected activity (note: enrolYear not enrollYear)
      const activity = activities.find(a => a.activityId === selectedActivity || a.activityId === parseInt(selectedActivity));
      const yearToUse = activity?.enrolYear || new Date().getFullYear();
      console.log('[ReportStudentListing] Activity selected, loading data for year:', yearToUse, 'from activity:', activity);
      loadReportData(selectedSchool, selectedActivity, yearToUse);
    }
  }, [selectedActivity]); // ✅ FIXED: Removed 'activities' from dependencies to prevent duplicate loads

  // ========== DATA LOADING FUNCTIONS ==========

  const loadUser = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        setUser(userData);
        console.log('[ReportStudentListing] User loaded:', userData.email);
      } else {
        console.error('[ReportStudentListing] No user found in localStorage');
        setError('User not found. Please login again.');
      }
    } catch (error) {
      console.error('[ReportStudentListing] Error loading user:', error);
      setError('Failed to load user information');
    }
  };

  const loadResourcePermissions = async () => {
    try {
      const resourceCode = '/report-student-listing';
      const data = await authorizationAPI.getPermissions(resourceCode);

      if (data.success && data.permissions) {
        const permissions = {
          resourceCode: data.resourceCode,
          hasAccess: data.permissions.hasAccess,
          dataScope: data.permissions.dataScope,
          submit: data.permissions.canSubmit,
          reopenSubmission: data.permissions.canReopenSubmission,
          manageSchool: data.permissions.canManageSchool
        };
        setResourcePermissions(permissions);
        console.log('[ReportStudentListing] Permissions loaded:', permissions);
      } else {
        console.error('[ReportStudentListing] Invalid permission response format');
        setResourcePermissions(null);
      }
    } catch (error) {
      console.error('[ReportStudentListing] Error loading permissions:', error);
      setResourcePermissions(null);
    }
  };

  const loadPPDs = async () => {
    try {
      console.log('[ReportStudentListing] Loading PPDs...');
      const response = await reportStudentListingAPI.getPPDs();

      if (response.success) {
        setPPDs(response.data);
        console.log('[ReportStudentListing] PPDs loaded:', response.data.length);

        // Auto-select if only one option
        if (response.data.length === 1) {
          setSelectedPPD(response.data[0].ppd);
        }
      } else {
        setError(response.message || 'Failed to load PPDs');
      }
    } catch (error) {
      console.error('[ReportStudentListing] Failed to load PPDs:', error);
      setError('Failed to load PPDs: ' + (error.response?.data?.message || error.message));
    }
  };

  const loadSchools = async (ppdCode) => {
    try {
      console.log('[ReportStudentListing] Loading schools for PPD:', ppdCode);
      const response = await reportStudentListingAPI.getSchools(ppdCode);

      if (response.success) {
        setSchools(response.data);
        console.log('[ReportStudentListing] Schools loaded:', response.data.length);

        // Auto-select if only one option
        if (response.data.length === 1) {
          setSelectedSchool(response.data[0].code);
        }
      } else {
        setError(response.message || 'Failed to load schools');
      }
    } catch (error) {
      console.error('[ReportStudentListing] Failed to load schools:', error);
      setError('Failed to load schools: ' + (error.response?.data?.message || error.message));
    }
  };

  const loadActivities = async (schoolCode) => {
    try {
      console.log('[ReportStudentListing] Loading activities for school:', schoolCode);
      const response = await reportStudentListingAPI.getActivities(schoolCode);

      if (response.success) {
        setActivities(response.data);
        console.log('[ReportStudentListing] Activities loaded:', response.data.length);
        console.log('[ReportStudentListing] Activity data:', JSON.stringify(response.data, null, 2));

        // Auto-select if only one option
        if (response.data.length === 1) {
          setSelectedActivity(response.data[0].activityId);
        }
      } else {
        setError(response.message || 'Failed to load activities');
      }
    } catch (error) {
      console.error('[ReportStudentListing] Failed to load activities:', error);
      setError('Failed to load activities: ' + (error.response?.data?.message || error.message));
    }
  };

  const loadSchoolInfo = async (schoolCode) => {
    try {
      console.log('[ReportStudentListing] Loading school info for:', schoolCode);
      const response = await reportStudentListingAPI.getSchoolInfo(schoolCode);

      if (response.success) {
        setSchoolInfo(response.data);
        console.log('[ReportStudentListing] School info loaded:', response.data);
      } else {
        console.error('[ReportStudentListing] Failed to load school info:', response.message);
        setSchoolInfo(null);
      }
    } catch (error) {
      console.error('[ReportStudentListing] Error loading school info:', error);
      setSchoolInfo(null);
    }
  };

  const loadReportData = async (schoolCode, activityId, year) => {
    if (!schoolCode || !activityId || !year) {
      Swal.fire({
        icon: 'warning',
        title: t('reports:common.messages.missingInformation'),
        text: t('reports:studentListing.messages.requiredFields'),
        confirmButtonColor: '#0066cc'
      });
      return;
    }

    try {
      setLoading(true);
      setError('');

      console.log('[ReportStudentListing] Loading report data...', {
        schoolCode: schoolCode,
        activityId: activityId,
        year: year
      });

      const response = await reportStudentListingAPI.getReportData(
        schoolCode,
        activityId,
        year
      );

      if (response.success) {
        setReportData(response.data);
        console.log('[ReportStudentListing] Report data loaded:', response.data);
      } else {
        setError(response.message || 'Failed to load report data');
        setReportData(null);
      }
    } catch (error) {
      console.error('[ReportStudentListing] Failed to load report data:', error);
      setError('Failed to load report data: ' + (error.response?.data?.message || error.message));
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  // ========== EVENT HANDLERS ==========

  const handlePPDChange = (e) => {
    setSelectedPPD(e.target.value);
  };

  const handleSchoolChange = (e) => {
    setSelectedSchool(e.target.value);
  };

  const handleActivityChange = (e) => {
    setSelectedActivity(parseInt(e.target.value));
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  // Check if user has restricted role (TEACHER/HEADMASTER)
  const isRestrictedRole = () => {
    return user && (user.role?.toUpperCase() === 'TEACHER' || user.role?.toUpperCase() === 'HEADMASTER');
  };

  // ========== EXPORT FUNCTIONS ==========

  const handleExportExcel = async () => {
    // Validation
    if (!selectedSchool || !selectedActivity) {
      Swal.fire({
        icon: 'warning',
        title: t('reports:common.messages.selectionRequired'),
        text: t('reports:studentListing.messages.requiredFields'),
        confirmButtonColor: '#0066cc'
      });
      return;
    }

    try {
      Swal.fire({
        title: t('reports:common.messages.generatingExcel'),
        html: t('reports:common.messages.pleaseWait'),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Get the year from reportData (already loaded), activity lookup, or fallback to current year
      const activity = activities.find(a => a.activityId === selectedActivity || a.activityId === parseInt(selectedActivity));
      const yearToUse = reportData?.year || activity?.enrolYear || new Date().getFullYear();

      await reportStudentListingAPI.exportToExcel({
        SchoolCode: selectedSchool,
        ActivityId: parseInt(selectedActivity),
        Year: parseInt(yearToUse) || new Date().getFullYear(),
        SplitBy: 0,
        IncludeSubtotals: true,
        IncludeGrandTotal: true,
        ShowPageHeaders: true,
        ApplyFormatting: true
      });

      Swal.fire({
        icon: 'success',
        title: t('reports:common.messages.exportSuccess'),
        text: t('reports:common.messages.excelDownloaded'),
        confirmButtonColor: '#28a745'
      });
    } catch (error) {
      console.error('Export error:', error);

      // Handle blob error responses
      let errorMessage = error.message || 'Unknown error';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          // If parsing fails, use the original error message
          console.error('Error parsing blob error:', parseError);
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      Swal.fire({
        icon: 'error',
        title: t('reports:common.messages.exportFailed'),
        text: errorMessage,
        confirmButtonColor: '#dc3545'
      });
    }
  };

  const handleExportPDF = async () => {
    if (!reportData || !reportData.students || reportData.students.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: t('reports:common.messages.noData'),
        text: t('reports:common.messages.noDataToExport'),
        confirmButtonColor: '#0066cc'
      });
      return;
    }

    try {
      console.log('[ReportStudentListing] Exporting to PDF via server-side...');

      Swal.fire({
        title: t('reports:common.messages.generatingPDF'),
        text: t('reports:common.messages.pleaseWaitPDF'),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Get the year from reportData (already loaded), activity lookup, or fallback to current year
      const activity = activities.find(a => a.activityId === selectedActivity || a.activityId === parseInt(selectedActivity));
      const yearToUse = reportData?.year || activity?.enrolYear || new Date().getFullYear();

      // Server-side PDF export with QuestPDF
      await reportStudentListingAPI.exportToPdf({
        schoolCode: selectedSchool,
        activityId: parseInt(selectedActivity),
        year: parseInt(yearToUse) || new Date().getFullYear(),
        splitBy: 0, // None
        includeSubtotals: true,
        includeGrandTotal: true,
        showPageHeaders: true,
        applyFormatting: true
      });

      Swal.fire({
        icon: 'success',
        title: t('reports:common.messages.exportSuccess'),
        confirmButtonColor: '#28a745'
      });

      console.log('[ReportStudentListing] PDF export completed');
    } catch (error) {
      console.error('[ReportStudentListing] PDF export failed:', error);

      // Handle blob error responses
      let errorMessage = t('reports:common.messages.exportFailedMessage');
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          // If parsing fails, use the original error message
          console.error('Error parsing blob error:', parseError);
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      Swal.fire({
        icon: 'error',
        title: t('reports:common.messages.exportFailed'),
        text: errorMessage,
        confirmButtonColor: '#dc3545'
      });
    }
  };

  // ========== HELPER FUNCTIONS ==========

  // Mask IC number to show only last 4 digits for data privacy
  const maskIC = (ic) => {
    if (!ic) return '';
    const icStr = String(ic);
    if (icStr.length <= 4) return icStr;
    return '********' + icStr.slice(-4);
  };

  const getSelectedActivityDisplay = () => {
    if (!selectedActivity || !activities.length) return '';
    const activity = activities.find(a => a.activityId === selectedActivity);
    return activity ? activity.activityDisplay : '';
  };

  // Handle Report Issue button click
  const handleReportIssueClick = async () => {
    try {
      Swal.fire({
        title: t('reports:common.messages.capturingScreenshot'),
        html: t('reports:common.messages.pleaseWaitScreenshot'),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        scrollY: -window.scrollY,
        scrollX: -window.scrollX,
        width: window.innerWidth,
        height: window.innerHeight
      });

      const imageData = canvas.toDataURL('image/png');
      setScreenshot(imageData);
      setShowReportIssueModal(true);
      Swal.close();
    } catch (error) {
      console.error('Error capturing screenshot:', error);
      Swal.fire({
        icon: 'error',
        title: t('reports:common.messages.captureFailed'),
        text: t('reports:common.messages.captureFailedMessage')
      });
    }
  };

  // ========== RENDER ==========

  return (
    <div className="report-student-listing">
      {/* Header */}
      <div className="container">
        {/* Header with Logo and Title */}
        <div className="header-container">
          <img className="logo" src="/KTC.png" alt="KTC" />
          <h2>
            {t('reports:studentListing.title')} ({t('reports:studentListing.titleEn')})
          </h2>
          <button className="header-btn btn-report-issue" onClick={handleReportIssueClick} title={t('common:reportIssue', { lng: 'en' })}>
            <i className="fas fa-bug"></i>
          </button>
        </div>

        {/* Filter and Info Section */}
        <div className="top-section">
          {/* Left Panel - Filters */}
          <div className="left-info">
            {/* Only show PPD dropdown for non-restricted roles */}
            {!isRestrictedRole() && (
              <div className="filter-group">
                <label><strong>{t('reports:studentListing.filters.district')}:</strong></label>
                <select value={selectedPPD} onChange={handlePPDChange}>
                  <option value="">{t('reports:studentListing.filters.selectPPD')}</option>
                  {ppds.map((ppd, index) => (
                    <option key={index} value={ppd.ppd}>{ppd.ppd}</option>
                  ))}
                </select>
              </div>
            )}

            {schoolInfo && (
              <>
                <p><strong>{t('reports:studentListing.info.dun')}:</strong> {schoolInfo.dunName} ({schoolInfo.dunCode})</p>
                <p><strong>{t('reports:studentListing.info.parlimen')}:</strong> {schoolInfo.parName} ({schoolInfo.parCode})</p>
              </>
            )}

            {/* Only show School dropdown for non-restricted roles */}
            {!isRestrictedRole() && (
              <div className="filter-group">
                <label><strong>{t('reports:studentListing.filters.school')}:</strong></label>
                <select
                  value={selectedSchool}
                  onChange={handleSchoolChange}
                  disabled={!selectedPPD || schools.length === 0}
                >
                  <option value="">{t('reports:studentListing.filters.selectSchool')}</option>
                  {schools.map((school, index) => (
                    <option key={index} value={school.code}>{school.displayName}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Show school name for restricted roles */}
            {isRestrictedRole() && schoolInfo && (
              <p><strong>{t('reports:studentListing.filters.school')}:</strong> {schoolInfo.schoolCode} - {schoolInfo.schoolName}</p>
            )}

            <div className="filter-group">
              <label><strong>{t('reports:studentListing.filters.activity')}:</strong></label>
              <select
                value={selectedActivity}
                onChange={handleActivityChange}
                disabled={!selectedSchool || activities.length === 0}
              >
                <option value="">{t('reports:studentListing.filters.selectActivity')}</option>
                {activities.map((activity) => (
                  <option key={activity.activityId} value={activity.activityId}>
                    {activity.activityDisplay}
                  </option>
                ))}
              </select>
            </div>

            {/* Year selection and retrieve button removed - data auto-loads when activity is selected */}
          </div>

          {/* Right Panel - User Info and Totals */}
          <div className="right-info">
            <p><strong>{t('reports:studentListing.info.user')}:</strong> {user?.email || t('common:loading')}</p>
            {reportData && (
              <>
                <p>
                  <strong>{t('reports:studentListing.info.studentCount')}:</strong> <span className="count-highlight">{reportData.totalJumlah}</span>
                  {' '}(<strong>{t('reports:studentListing.info.male')}:</strong> <span className="count-highlight">{reportData.totalLelaki}</span>
                  {' '}<strong>{t('reports:studentListing.info.female')}:</strong> <span className="count-highlight">{reportData.totalPerempuan}</span>)
                </p>
                <p><strong>{t('reports:studentListing.info.reportDate')}:</strong> {new Date(reportData.reportDate).toLocaleString()}</p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      {reportData && reportData.students && reportData.students.length > 0 && (
        <div className="action-buttons-container">
          <button type="button" className="action-btn btn-refresh" onClick={handleRefresh}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
            </svg>
            {t('reports:common.actions.refresh')}
          </button>
          <button type="button" className="action-btn btn-excel" onClick={handleExportExcel}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M12,19L8,15H10.5V12H13.5V15H16L12,19Z"/>
            </svg>
            {t('reports:common.actions.exportExcel')}
          </button>
          <button type="button" className="action-btn btn-pdf" onClick={handleExportPDF}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M10.92,12.31C10.68,11.54 10.15,9.08 11.55,9.04C12.95,9 12.03,12.16 12.03,12.16C12.42,13.65 14.05,14.72 14.05,14.72C14.55,14.57 17.4,14.24 17,15.72C16.57,17.2 13.5,15.81 13.5,15.81C11.55,15.95 10.09,16.47 10.09,16.47C8.96,18.58 7.64,19.5 7.1,18.61C6.43,17.5 9.23,16.07 9.23,16.07C10.68,13.72 10.92,12.31 10.92,12.31Z"/>
            </svg>
            {t('reports:common.actions.exportPDF')}
          </button>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      {/* ag-grid Table */}
      {reportData && reportData.students && reportData.students.length > 0 && (
        <div style={{ marginTop: '20px', padding: '0 20px', maxWidth: '100%', overflowX: 'auto' }}>
          <div className="ag-theme-alpine" style={{ height: '700px', width: '100%', minWidth: '1600px' }}>
            <AgGridReact
              rowData={reportData.students}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              gridOptions={gridOptions}
              onGridReady={onGridReady}
            />
          </div>
        </div>
      )}

      {/* Report Issue Modal */}
      {showReportIssueModal && (
        <ReportIssueModal
          show={showReportIssueModal}
          onClose={() => setShowReportIssueModal(false)}
          screenshot={screenshot}
          context={{
            screen: 'Report Student Listing',
            selectedPPD,
            selectedSchool,
            selectedActivity,
            schoolInfo,
            reportData
          }}
        />
      )}
    </div>
  );
};

export default ReportStudentListing;
